<script setup>
import { ref, computed, onMounted, reactive, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers, url } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useToggle } from '@vueuse/core';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import TalkviaOpenAIConfigAPI from 'dashboard/api/talkviaOpenAIConfig';
import InboxesAPI from 'dashboard/api/inboxes';
import { TALKVIA_AI_WEBHOOK_URL } from 'dashboard/helper/talkviaOpenAIUrl';
import {
  ASAAS_APP_ID,
  asaasIntegrationLinkKey,
  buildAsaasIntegrationOptions,
} from '../asaasIntegrationLink';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { useChannelIcon } from 'dashboard/components-next/icon/provider';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AvatarLibrary from 'dashboard/components-next/avatar/AvatarLibrary.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');
const integrations = useMapGetter('integrations/getAppIntegrations');
const agentBots = useMapGetter('agentBots/getBots');
const allInboxes = useMapGetter('inboxes/getAllInboxes');
const localCopy = {
  integrationLink: 'Integração vinculada',
  integrationHint:
    'Escolha quais serviços este agente poderá consultar durante o atendimento.',
  integrationSelect: 'Serviço usado pelo agente',
  integrationSelectPlaceholder: 'Selecione uma integração',
  integrationConfiguration: 'Configuração da integração',
  noIntegrationLink: 'Nenhuma integração vinculada',
  noIntegrationHint: 'O agente funcionará sem consultar serviços externos.',
  banks: 'Bancos',
  banksHint: 'Consulte propostas e produtos bancários.',
  allActiveBanks: 'Todos os bancos ativos',
  allActiveBanksHint: 'Inclui automaticamente todas as conexões ativas.',
  selectBanks: 'Selecionar bancos',
  selectBanksHint: 'Escolha somente os bancos que este agente deve consultar.',
  selectAtLeastOneBank: 'Selecione pelo menos um banco.',
  activeBank: 'Ativo',
  inactiveBank: 'Inativo',
  bankNotConnected: 'Não conectado',
  providers: 'Provedores',
  providersHint: 'Conecte o agente a um provedor de gestão.',
  asaasHint:
    'A IA consulta cobranças somente do CPF/CNPJ informado pelo cliente, sem criar ou alterar cobranças.',
  talkviaAi: 'IA Talkvia',
  talkviaAiHint:
    'Usa o motor de IA da Talkvia para atender seus clientes automaticamente, sem configurações técnicas.',
  talkviaAiActive: 'Ativa',
  talkviaAiInactive: 'Ativar IA',
  credentialsTitle: 'Credenciais de integração',
  credentialsDescription:
    'Use estas chaves para conectar o agente com segurança a outros sistemas.',
  promptId: 'Instruções da IA',
  promptFieldLabel: 'Prompt utilizado pelo agente',
  promptDescription:
    'Escolha como a IA Talkvia vai atender. O prompt padrão já vem pronto para o seu segmento.',
  promptModeSystem: 'Prompt padrão Talkvia',
  promptModeSystemHint:
    'Recomendado. Você escolhe só o nome da IA; a Talkvia cuida das instruções.',
  promptModeCustom: 'Prompt personalizado',
  promptModeCustomHint: 'Use um prompt que você criou na Biblioteca de IA.',
  aiNameLabel: 'Nome da IA',
  aiNamePlaceholder: 'Ex.: Bia',
  aiNameRequired: 'Informe o nome da IA',
  systemSegmentPrefix: 'Modelo aplicado:',
  systemSegmentHint: {
    geral:
      'Atendimento geral: entende o pedido, responde com a base de conhecimento e encaminha para a equipe.',
    provedor:
      'Provedor de Internet: segunda via, situação financeira, liberação de acesso e suporte técnico básico.',
    corban:
      'Corban: explica o consignado, segue a autorização do cliente e apresenta as ofertas dos bancos.',
  },
  systemSegmentLabel: {
    geral: 'Atendimento geral',
    provedor: 'Provedor de Internet',
    corban: 'Corban',
  },
  promptIdPlaceholder: 'Selecione um prompt',
  noPrompt: 'Nenhum prompt vinculado',
  legacyPrompt: 'Prompt legado',
  connectionTitle: 'Conexão e automação',
  connectionDescription:
    'Ative a IA Talkvia ou defina o endpoint responsável pelo atendimento.',
  identityTitle: 'Identidade do agente',
  identityDescription:
    'Nome, descrição e imagem usados para identificar o agente.',
  credentialsReadyTitle: 'Agente criado e credenciais prontas',
  credentialsReadyDescription:
    'Copie estas credenciais antes de concluir. Por segurança, mantenha-as somente em um local protegido.',
  inboxesTitle: 'Caixas de entrada',
  inboxesDescription:
    'Selecione uma ou mais caixas para esta assistente atender automaticamente.',
  inboxAlreadyAssigned: 'Conectada a outra assistente',
  noInboxes: 'Nenhuma caixa de entrada disponível.',
  inboxPicker: 'Adicionar caixa de entrada',
  inboxPickerPlaceholder: 'Selecione uma caixa para adicionar',
  inboxPickerSearch: 'Buscar caixa de entrada',
  inboxPickerEmpty: 'Nenhuma caixa encontrada',
  selectedInboxes: 'Caixas selecionadas',
  noSelectedInboxes: 'Nenhuma caixa selecionada.',
  removeInbox: 'Remover caixa',
  inboxSyncWarning:
    'A assistente foi salva, mas não foi possível atualizar todas as caixas de entrada.',
  done: 'Concluir',
};

const formState = reactive({
  botName: '',
  botDescription: '',
  botUrl: '',
  promptId: '',
  promptMode: 'system',
  aiName: '',
  talkviaAi: false,
  integrationLink: '',
  selectedBankHookIds: [],
  selectedInboxIds: [],
  botAvatar: null,
  botAvatarUrl: '',
});

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');
const botSecret = ref('');
const openAIPrompts = ref([]);
const openAIFeatures = ref({});
const isSavingInboxes = ref(false);
const inboxPickerId = ref('');

const canUseTalkviaAI = computed(
  () =>
    currentAccount.value?.features?.[FEATURE_FLAGS.TALKVIA_AI_AGENT] === true
);

const isTalkviaAISelected = computed(
  () => canUseTalkviaAI.value && formState.talkviaAi
);

const validationRules = computed(() => ({
  botName: {
    required: helpers.withMessage(
      () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
      required
    ),
  },
  aiName:
    isTalkviaAISelected.value && formState.promptMode === 'system'
      ? {
          required: helpers.withMessage(
            () => localCopy.aiNameRequired,
            required
          ),
        }
      : {},
  promptId:
    isTalkviaAISelected.value && formState.promptMode === 'custom'
      ? {
          required: helpers.withMessage(
            () => 'Selecione um prompt para a IA Talkvia',
            required
          ),
        }
      : {},
  botUrl: isTalkviaAISelected.value
    ? {}
    : {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.URL'),
          required
        ),
        url: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.VALID_URL'),
          url
        ),
      },
}));

const v$ = useVuelidate(validationRules, formState);

const isLoading = computed(
  () =>
    isSavingInboxes.value ||
    (props.type === MODAL_TYPES.CREATE
      ? uiFlags.value.isCreating
      : uiFlags.value.isUpdating)
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  }

  return props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION');
  }
  return '';
});

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const closeButtonLabel = computed(() =>
  showAccessToken.value ? localCopy.done : t('AGENT_BOTS.FORM.CANCEL')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const botUrlError = computed(() =>
  v$.value.botUrl.$error ? v$.value.botUrl.$errors[0]?.$message : ''
);

const promptIdError = computed(() =>
  v$.value.promptId.$error ? v$.value.promptId.$errors[0]?.$message : ''
);

const showAccessTokenInput = computed(
  () =>
    showAccessToken.value ||
    props.type === MODAL_TYPES.EDIT ||
    accessToken.value
);

const sortedInboxes = computed(() =>
  [...(allInboxes.value || [])].sort((leftInbox, rightInbox) =>
    String(leftInbox.name || '').localeCompare(
      String(rightInbox.name || ''),
      'pt-BR',
      { sensitivity: 'base' }
    )
  )
);

const assignedBotByInboxId = computed(() => {
  const assignments = new Map();
  (agentBots.value || []).forEach(bot => {
    (bot.inbox_ids || []).forEach(inboxId => {
      assignments.set(String(inboxId), bot);
    });
  });
  return assignments;
});

const inboxAssignedBot = inbox =>
  assignedBotByInboxId.value.get(String(inbox.id));

const isInboxAssignedToAnotherBot = inbox => {
  const assignedBot = inboxAssignedBot(inbox);
  return (
    assignedBot && Number(assignedBot.id) !== Number(props.selectedBot?.id)
  );
};

const inboxAssignmentLabel = inbox => {
  const assignedBot = inboxAssignedBot(inbox);
  return assignedBot?.name
    ? `${localCopy.inboxAlreadyAssigned}: ${assignedBot.name}`
    : localCopy.inboxAlreadyAssigned;
};

const selectedInboxes = computed(() =>
  formState.selectedInboxIds.map(inboxId => {
    const inbox = sortedInboxes.value.find(
      currentInbox => String(currentInbox.id) === String(inboxId)
    );
    return inbox || { id: inboxId, name: `Caixa #${inboxId}` };
  })
);

// A lista de caixas do store usa camelCase; o ícone do canal espera snake_case.
const inboxChannelIcon = inbox =>
  useChannelIcon({
    channel_type: inbox.channel_type || inbox.channelType,
    provider: inbox.provider,
    medium: inbox.medium,
    voice_enabled: inbox.voice_enabled ?? inbox.voiceEnabled,
    additional_attributes:
      inbox.additional_attributes || inbox.additionalAttributes,
  }).value;

const inboxPickerOptions = computed(() =>
  sortedInboxes.value
    .filter(inbox => !formState.selectedInboxIds.includes(String(inbox.id)))
    .map(inbox => ({
      value: String(inbox.id),
      icon: inboxChannelIcon(inbox),
      label: isInboxAssignedToAnotherBot(inbox)
        ? `${inbox.name} — ${inboxAssignmentLabel(inbox)}`
        : inbox.name,
    }))
);

watch(inboxPickerId, inboxId => {
  if (!inboxId) return;
  if (!formState.selectedInboxIds.includes(String(inboxId))) {
    formState.selectedInboxIds.push(String(inboxId));
  }
  inboxPickerId.value = '';
});

const providerLabels = {
  c6: 'C6',
  handplus: 'Hand+',
  facta: 'Facta',
  uy3: 'Uy3',
  bmp: 'BMP',
  cellcoin: 'CellCoin',
  v8: 'V8',
  prata: 'Prata',
  presenca_bank: 'Presença Bank',
  soma: 'Soma',
  bull: 'Bull',
  bmg: 'Banco BMG',
  fintech_corban: 'Fintech do Corban',
  ixcsoft: 'IXCSoft',
  mkauth: 'MkAuth',
};

const BANKING_PRODUCTS = ['CLT', 'FGTS'];
const BANK_PROVIDER_CATALOG = [
  { provider: 'c6', label: 'C6' },
  { provider: 'handplus', label: 'Hand+' },
  { provider: 'facta', label: 'Facta' },
  { provider: 'presenca_bank', label: 'Presença Bank' },
  { provider: 'v8', label: 'V8' },
  { provider: 'soma', label: 'Soma' },
  { provider: 'bull', label: 'Bull' },
  { provider: 'bmg', label: 'Banco BMG' },
  { provider: 'fintech_corban', label: 'Fintech do Corban' },
];

const normalizeBankProductValues = values =>
  (Array.isArray(values) ? values : [values])
    .flatMap(value =>
      String(value || '')
        .split(/[;,|]/)
        .map(item => item.trim().toUpperCase())
        .filter(Boolean)
    )
    .filter(product => BANKING_PRODUCTS.includes(product));

const isHookEnabled = hook =>
  hook?.status === true || hook?.status === 'enabled';

const activeHooksFor = appId =>
  integrations.value
    .find(integration => integration.id === appId)
    ?.hooks?.filter(isHookEnabled) || [];

const bankingHooks = computed(() =>
  (
    integrations.value.find(integration => integration.id === 'banking')
      ?.hooks || []
  ).filter(hook => hook.settings?.provider !== '_product_config')
);

const bankOptions = computed(() => {
  const groupedByProvider = new Map(
    BANK_PROVIDER_CATALOG.map(option => [
      option.provider,
      {
        ...option,
        hookIds: [],
        products: [],
        configured: false,
        active: false,
      },
    ])
  );

  bankingHooks.value.forEach(hook => {
    const rawProvider = hook.settings?.provider;
    const provider = rawProvider === 'prata' ? 'presenca_bank' : rawProvider;
    if (!provider) return;

    const current = groupedByProvider.get(provider) || {
      provider,
      label: providerLabels[provider] || provider,
      hookIds: [],
      products: [],
      configured: false,
      active: false,
    };
    current.configured = true;
    current.active ||= isHookEnabled(hook);
    if (isHookEnabled(hook)) current.hookIds.push(String(hook.id));
    current.products.push(
      ...normalizeBankProductValues(
        hook.settings?.produtos || hook.settings?.produto
      )
    );
    groupedByProvider.set(provider, current);
  });

  return [...groupedByProvider.values()].map(option => ({
    ...option,
    products: [...new Set(option.products)],
    displayLabel: option.products.length
      ? `${option.label} (${[...new Set(option.products)].join(', ')})`
      : option.label,
  }));
});

const activeBankOptions = computed(() =>
  bankOptions.value.filter(option => option.active)
);

const hasActiveBankIntegrations = computed(
  () => activeBankOptions.value.length > 0
);

const erpOptions = computed(() =>
  activeHooksFor('erp').map(hook => {
    const provider = hook.settings?.provider;
    return {
      value: `erp:${hook.id}`,
      label: providerLabels[provider] || provider,
      config: {
        key: `erp:${hook.id}`,
        app_id: 'erp',
        type: 'erp',
        mode: 'single',
        hook_id: hook.id,
        provider,
        integration_name: 'Integrações IXCSoft e MkAuth',
      },
    };
  })
);

// Asaas allows a single hook per account; the runtime reads it by hook_id.
const asaasOptions = computed(() =>
  buildAsaasIntegrationOptions(
    activeHooksFor(ASAAS_APP_ID),
    localCopy.asaasHint
  )
);

const providerIntegrationOptions = computed(() => [
  ...erpOptions.value,
  ...asaasOptions.value,
]);

const hasActiveProviderIntegrations = computed(
  () => providerIntegrationOptions.value.length > 0
);

const integrationType = computed({
  get() {
    return formState.integrationLink.startsWith('banking:')
      ? 'banking'
      : formState.integrationLink;
  },
  set(value) {
    if (value === 'banking') {
      formState.integrationLink = formState.integrationLink.startsWith(
        'banking:'
      )
        ? formState.integrationLink
        : 'banking:all_active';
      return;
    }
    formState.integrationLink = value;
  },
});

const integrationTypeOptions = computed(() => {
  const options = [{ value: '', label: localCopy.noIntegrationLink }];
  if (hasActiveBankIntegrations.value) {
    options.push({ value: 'banking', label: localCopy.banks });
  }
  options.push(
    ...providerIntegrationOptions.value.map(option => ({
      value: option.value,
      label: option.label,
    }))
  );
  return options;
});

const selectedErpOption = computed(() =>
  providerIntegrationOptions.value.find(
    option => option.value === formState.integrationLink
  )
);

watch(
  [
    () => formState.integrationLink,
    hasActiveBankIntegrations,
    hasActiveProviderIntegrations,
  ],
  ([integrationLink, hasActiveBanks, hasActiveProviders]) => {
    if (!hasActiveBanks && integrationLink.startsWith('banking:')) {
      formState.integrationLink = '';
      formState.selectedBankHookIds = [];
    }

    if (
      !hasActiveProviders &&
      (integrationLink.startsWith('erp:') ||
        integrationLink.startsWith('asaas:'))
    ) {
      formState.integrationLink = '';
    }
  }
);

const promptOptions = computed(() =>
  openAIPrompts.value
    .filter(prompt => !prompt.system_default)
    .map(prompt => ({
      value: String(prompt.id),
      label: prompt.active
        ? `${prompt.name} - ${prompt.category_label}`
        : `${prompt.name} - ${prompt.category_label} (inativo)`,
      prompt,
    }))
);

const selectedOpenAIPrompt = computed(
  () =>
    promptOptions.value.find(option => option.value === formState.promptId)
      ?.prompt || null
);

const hasLegacyPromptId = computed(
  () => formState.promptId && !selectedOpenAIPrompt.value
);

const legacyPromptLabel = computed(
  () => `${localCopy.legacyPrompt} - ${formState.promptId}`
);

const promptSelectOptions = computed(() => {
  const options = [{ value: '', label: localCopy.noPrompt }];
  if (hasLegacyPromptId.value) {
    options.push({
      value: formState.promptId,
      label: legacyPromptLabel.value,
    });
  }
  return [...options, ...promptOptions.value];
});

const selectedBankOptions = computed(() =>
  bankOptions.value.filter(option =>
    option.hookIds.some(id => formState.selectedBankHookIds.includes(id))
  )
);

const selectedIntegrationLink = computed(() => {
  if (formState.integrationLink === 'banking:all_active') {
    return {
      key: 'banking:all_active',
      app_id: 'banking',
      type: 'banking',
      mode: 'all_active',
      integration_name: 'Integrações com bancos - todos os bancos ativos',
    };
  }

  if (formState.integrationLink === 'banking:selected') {
    const selectedOptions = selectedBankOptions.value;
    const hookIds = [
      ...new Set(selectedOptions.flatMap(option => option.hookIds)),
    ];
    if (!hookIds.length) return null;

    const providers = selectedOptions.map(option => option.provider);
    const providerNames = selectedOptions.map(option => option.label);
    const integrationLink = {
      key: 'banking:selected',
      app_id: 'banking',
      type: 'banking',
      mode: 'selected',
      hook_ids: hookIds,
      providers,
      integration_name: `Integrações com bancos - ${providerNames.join(', ')}`,
    };

    // Preserve the original single-bank webhook contract while also sending
    // the array fields used by the multi-bank flow.
    if (selectedOptions.length === 1 && hookIds.length === 1) {
      const [selectedOption] = selectedOptions;
      return {
        ...integrationLink,
        mode: 'single',
        hook_id: hookIds[0],
        provider: selectedOption.provider,
        produto: selectedOption.products[0],
        produtos: selectedOption.products,
      };
    }

    return integrationLink;
  }

  return (
    providerIntegrationOptions.value.find(
      option => option.value === formState.integrationLink
    )?.config || null
  );
});

// Segmento do prompt padrão: segue a integração escolhida no agente e, sem integração,
// o segmento da conta calculado pelo servidor.
const systemPromptSegment = computed(() => {
  const link = selectedIntegrationLink.value;
  if (link?.app_id === 'erp' && ['ixcsoft', 'mkauth'].includes(link.provider))
    return 'provedor';
  if (link?.app_id === 'banking') return 'corban';
  return openAIFeatures.value.system_prompt_segment || 'geral';
});

const bankSelectionError = computed(
  () =>
    formState.integrationLink === 'banking:selected' &&
    !selectedBankOptions.value.length
);

const selectedBankCount = computed(() => selectedBankOptions.value.length);

const availableBankCountLabel = computed(() =>
  activeBankOptions.value.length === 1
    ? '1 banco ativo'
    : `${activeBankOptions.value.length} bancos ativos`
);

const selectedBankCountLabel = computed(() =>
  selectedBankCount.value === 1
    ? '1 banco selecionado'
    : `${selectedBankCount.value} bancos selecionados`
);

const resetForm = () => {
  inboxPickerId.value = '';
  Object.assign(formState, {
    botName: '',
    botDescription: '',
    botUrl: '',
    promptId: '',
    promptMode: 'system',
    aiName: '',
    talkviaAi: false,
    integrationLink: '',
    selectedBankHookIds: [],
    selectedInboxIds: [],
    botAvatar: null,
    botAvatarUrl: '',
  });
  v$.value.$reset();
};

const removeInbox = inboxId => {
  formState.selectedInboxIds = formState.selectedInboxIds.filter(
    selectedId => String(selectedId) !== String(inboxId)
  );
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const syncInboxAssignments = async botId => {
  const selectedInboxIds = new Set(formState.selectedInboxIds.map(String));
  const requests = sortedInboxes.value.flatMap(inbox => {
    const assignedBot = inboxAssignedBot(inbox);
    const isSelected = selectedInboxIds.has(String(inbox.id));
    const isAssignedToThisBot = Number(assignedBot?.id) === Number(botId);

    if (isSelected && !isAssignedToThisBot) {
      return [InboxesAPI.setAgentBot(inbox.id, botId)];
    }

    if (!isSelected && isAssignedToThisBot) {
      return [InboxesAPI.setAgentBot(inbox.id, null)];
    }

    return [];
  });

  if (!requests.length) return;

  isSavingInboxes.value = true;
  try {
    await Promise.all(requests);
    await store.dispatch('agentBots/get');
  } finally {
    isSavingInboxes.value = false;
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (bankSelectionError.value) return;
  if (showAccessToken.value) return;

  const usesSystemPrompt = formState.promptMode === 'system';
  const promptId = usesSystemPrompt ? null : formState.promptId || null;
  const promptName = promptId
    ? selectedOpenAIPrompt.value?.name ||
      props.selectedBot?.bot_config?.talkvia_openai_prompt_name ||
      ''
    : '';

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    outgoing_url: isTalkviaAISelected.value
      ? TALKVIA_AI_WEBHOOK_URL
      : formState.botUrl,
    bot_type: 'webhook',
    bot_config: {
      talkvia_ai_enabled: isTalkviaAISelected.value,
      talkvia_prompt_mode: usesSystemPrompt ? 'system' : 'custom',
      talkvia_ai_name: usesSystemPrompt ? formState.aiName.trim() : '',
      talkvia_openai_prompt_id: promptId || '',
      talkvia_openai_prompt_name: promptName,
      webhook_integration_link: selectedIntegrationLink.value,
    },
    avatar: formState.botAvatar,
  };

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };

    const response = await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    const savedBotId = isCreate ? response?.id : props.selectedBot.id;
    try {
      await syncInboxAssignments(savedBotId);
    } catch (error) {
      useAlert(localCopy.inboxSyncWarning);
    }

    const alertKey = isCreate
      ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    // Show access token and secret after creation
    if (isCreate) {
      const {
        access_token: responseAccessToken,
        secret: responseSecret,
        id,
      } = response || {};

      if (id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        botSecret.value = responseSecret || '';
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        botSecret.value = '';
        dialogRef.value.close();
      }
    } else {
      dialogRef.value.close();
    }

    resetForm();
  } catch (error) {
    const errorKey = isCreate
      ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      outgoing_url: botUrl,
      thumbnail,
      bot_config: botConfig,
      access_token: botAccessToken,
      secret: botSecretValue,
    } = props.selectedBot;
    const isTalkviaBot =
      botConfig?.talkvia_ai_enabled === true ||
      botConfig?.talkvia_ai_enabled === 'true';

    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.talkviaAi = isTalkviaBot && canUseTalkviaAI.value;
    formState.botUrl = isTalkviaBot
      ? ''
      : botUrl || botConfig?.webhook_url || '';
    formState.promptId = String(
      botConfig?.talkvia_openai_prompt_id ||
        botConfig?.id_prompt ||
        botConfig?.prompt_id ||
        ''
    );
    // Agentes antigos com prompt escolhido continuam personalizados.
    formState.promptMode =
      botConfig?.talkvia_prompt_mode === 'system' || !formState.promptId
        ? 'system'
        : 'custom';
    formState.aiName = botConfig?.talkvia_ai_name || '';
    const integrationLink = botConfig?.webhook_integration_link;
    if (
      integrationLink?.app_id === 'banking' &&
      integrationLink?.mode === 'single'
    ) {
      formState.integrationLink = 'banking:selected';
      formState.selectedBankHookIds = integrationLink.hook_id
        ? [String(integrationLink.hook_id)]
        : [];
    } else {
      formState.integrationLink =
        integrationLink?.key || asaasIntegrationLinkKey(integrationLink);
      formState.selectedBankHookIds = (integrationLink?.hook_ids || []).map(
        String
      );
    }
    formState.botAvatarUrl = thumbnail || '';
    formState.selectedInboxIds = (props.selectedBot.inbox_ids || []).map(
      String
    );

    if (props.type === MODAL_TYPES.EDIT) {
      if (botAccessToken) accessToken.value = botAccessToken;
      if (botSecretValue) botSecret.value = botSecretValue;
    }
  } else {
    resetForm();
  }
};

const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
};

const onCopySecret = async value => {
  await copyTextToClipboard(value || botSecret.value);
  useAlert(t('AGENT_BOTS.SECRET.COPY_SUCCESS'));
};

const onResetSecret = async () => {
  const response = await store.dispatch(
    'agentBots/resetSecret',
    props.selectedBot.id
  );
  if (response) {
    botSecret.value = response.secret;
    useAlert(t('AGENT_BOTS.SECRET.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.SECRET.RESET_ERROR'));
  }
};

const onResetToken = async () => {
  const response = await store.dispatch(
    'agentBots/resetAccessToken',
    props.selectedBot.id
  );
  if (response) {
    accessToken.value = response.access_token;
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
  }
};

const fetchOpenAIPrompts = async () => {
  try {
    const { data } = await TalkviaOpenAIConfigAPI.get();
    openAIPrompts.value = data.prompts || [];
    openAIFeatures.value = data.features || {};
  } catch (error) {
    openAIPrompts.value = [];
  }
};

const closeModal = () => {
  if (!showAccessToken.value) v$.value?.$reset();
  accessToken.value = '';
  botSecret.value = '';
  toggleAccessToken(false);
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

watch(canUseTalkviaAI, initializeForm);

onMounted(() => {
  store.dispatch('integrations/get');
  store.dispatch('inboxes/getFromNetwork');
  fetchOpenAIPrompts();
});

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :width="showAccessToken && type === MODAL_TYPES.CREATE ? '3xl' : '5xl'"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @confirm="handleSubmit"
    @close="closeModal"
  >
    <div class="flex max-h-[calc(100dvh-10rem)] min-h-0 flex-col gap-4">
      <div class="min-h-0 flex-1 overflow-y-auto pr-1">
        <div
          class="grid grid-cols-1 gap-6"
          :class="
            showAccessToken && type === MODAL_TYPES.CREATE
              ? 'mx-auto max-w-2xl'
              : 'mx-auto w-full max-w-4xl'
          "
        >
          <div
            v-if="showAccessToken && type === MODAL_TYPES.CREATE"
            class="flex items-start gap-3 rounded-xl border border-n-teal-7/50 bg-n-teal-3/30 p-4"
          >
            <span
              class="grid size-10 shrink-0 place-content-center rounded-xl bg-n-teal-4 text-n-teal-11"
            >
              <span class="i-lucide-circle-check-big size-5" />
            </span>
            <div>
              <p class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ localCopy.credentialsReadyTitle }}
              </p>
              <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
                {{ localCopy.credentialsReadyDescription }}
              </p>
            </div>
          </div>

          <template v-if="!showAccessToken || type === MODAL_TYPES.EDIT">
            <section
              class="order-1 grid min-w-0 grid-cols-1 gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5 lg:grid-cols-[10rem_minmax(0,1fr)]"
            >
              <div class="lg:col-span-2">
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ localCopy.identityTitle }}
                </p>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ localCopy.identityDescription }}
                </p>
              </div>

              <div class="flex flex-col items-start lg:row-span-2">
                <span class="mb-2 text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
                </span>
                <Avatar
                  :src="formState.botAvatarUrl"
                  :name="formState.botName"
                  :size="68"
                  allow-upload
                  icon-name="i-lucide-bot-message-square"
                  @upload="handleImageUpload"
                  @delete="handleAvatarDelete"
                />
                <div class="mt-3">
                  <AvatarLibrary
                    :disabled="isLoading"
                    @select="handleImageUpload"
                  />
                </div>
              </div>

              <Input
                id="bot-name"
                v-model="formState.botName"
                :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
                :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
                :message="botNameError"
                :message-type="botNameError ? 'error' : 'info'"
                @blur="v$.botName.$touch()"
              />

              <TextArea
                id="bot-description"
                v-model="formState.botDescription"
                :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
                :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
              />
            </section>

            <section
              class="order-3 flex min-w-0 flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ localCopy.connectionTitle }}
                </p>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ localCopy.connectionDescription }}
                </p>
              </div>

              <label
                v-if="canUseTalkviaAI"
                class="group relative flex cursor-pointer items-start gap-4 overflow-hidden rounded-xl border p-4 text-sm transition-all focus-within:ring-2 focus-within:ring-n-brand/30"
                :class="
                  isTalkviaAISelected
                    ? 'border-n-brand bg-n-brand/10 shadow-[0_0_0_1px_var(--color-n-brand)]'
                    : 'border-n-weak bg-n-solid-2 hover:border-n-slate-8 hover:bg-n-alpha-1'
                "
              >
                <input
                  v-model="formState.talkviaAi"
                  type="checkbox"
                  class="sr-only"
                />
                <span
                  class="grid size-11 shrink-0 place-content-center rounded-xl transition-colors"
                  :class="
                    isTalkviaAISelected
                      ? 'bg-n-brand text-white shadow-sm'
                      : 'bg-n-alpha-2 text-n-slate-11 group-hover:text-n-brand'
                  "
                >
                  <span class="i-lucide-sparkles size-5" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="flex flex-wrap items-center gap-2">
                    <span class="text-base font-semibold text-n-slate-12">
                      {{ localCopy.talkviaAi }}
                    </span>
                    <span
                      class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                      :class="
                        isTalkviaAISelected
                          ? 'bg-n-brand text-white'
                          : 'bg-n-alpha-2 text-n-slate-10'
                      "
                    >
                      {{
                        isTalkviaAISelected
                          ? localCopy.talkviaAiActive
                          : localCopy.talkviaAiInactive
                      }}
                    </span>
                  </span>
                  <span class="mt-1 block text-xs leading-5 text-n-slate-11">
                    {{ localCopy.talkviaAiHint }}
                  </span>
                </span>
                <span
                  class="grid size-6 shrink-0 place-content-center rounded-full border transition-colors"
                  :class="
                    isTalkviaAISelected
                      ? 'border-n-brand bg-n-brand text-white'
                      : 'border-n-strong bg-n-solid-1 text-transparent group-hover:border-n-brand'
                  "
                >
                  <span class="i-lucide-check size-3.5" />
                </span>
              </label>

              <Input
                v-if="!isTalkviaAISelected"
                id="bot-url"
                v-model="formState.botUrl"
                :label="$t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')"
                :placeholder="$t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')"
                :message="botUrlError"
                :message-type="botUrlError ? 'error' : 'info'"
                custom-input-class="!bg-n-surface-1"
                @blur="v$.botUrl.$touch()"
              />
            </section>

            <section
              class="order-3 flex min-w-0 flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ localCopy.promptId }}
                </p>
                <p class="mt-1 text-xs leading-5 text-n-slate-11">
                  {{ localCopy.promptDescription }}
                </p>
              </div>

              <div class="grid gap-2 sm:grid-cols-2" role="radiogroup">
                <button
                  v-for="mode in ['system', 'custom']"
                  :key="mode"
                  type="button"
                  role="radio"
                  class="flex flex-col gap-1 rounded-xl border p-3 text-left transition-colors"
                  :class="
                    formState.promptMode === mode
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak hover:bg-n-alpha-1'
                  "
                  :aria-checked="formState.promptMode === mode"
                  @click="formState.promptMode = mode"
                >
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      mode === 'system'
                        ? localCopy.promptModeSystem
                        : localCopy.promptModeCustom
                    }}
                  </span>
                  <span class="text-xs leading-5 text-n-slate-11">
                    {{
                      mode === 'system'
                        ? localCopy.promptModeSystemHint
                        : localCopy.promptModeCustomHint
                    }}
                  </span>
                </button>
              </div>

              <template v-if="formState.promptMode === 'system'">
                <Input
                  v-model="formState.aiName"
                  :label="localCopy.aiNameLabel"
                  :placeholder="localCopy.aiNamePlaceholder"
                  :message="
                    v$.aiName.$error ? v$.aiName.$errors[0]?.$message : ''
                  "
                  :message-type="v$.aiName.$error ? 'error' : 'info'"
                  maxlength="80"
                  @blur="v$.aiName.$touch()"
                />
                <div
                  class="flex items-start gap-2 rounded-lg bg-n-alpha-1 px-3 py-2.5 text-xs leading-5 text-n-slate-11"
                >
                  <span class="i-lucide-sparkles mt-0.5 size-4 shrink-0" />
                  <span>
                    <strong class="text-n-slate-12">
                      {{ localCopy.systemSegmentPrefix }}
                      {{ localCopy.systemSegmentLabel[systemPromptSegment] }}.
                    </strong>
                    {{ localCopy.systemSegmentHint[systemPromptSegment] }}
                  </span>
                </div>
              </template>

              <label
                v-else
                class="flex flex-col gap-1.5 text-sm text-n-slate-12"
              >
                <span class="font-medium">{{
                  localCopy.promptFieldLabel
                }}</span>
                <Select
                  v-model="formState.promptId"
                  :options="promptSelectOptions"
                  :error="promptIdError"
                  class="!w-full [&>select]:!w-full"
                  @focusout="v$.promptId.$touch()"
                />
                <span v-if="promptIdError" class="text-xs text-n-ruby-9">
                  {{ promptIdError }}
                </span>
              </label>
            </section>

            <section
              class="order-3 flex min-w-0 flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ localCopy.integrationLink }}
                </p>
                <p class="mt-1 text-xs leading-5 text-n-slate-11">
                  {{ localCopy.integrationHint }}
                </p>
              </div>

              <fieldset class="flex flex-col gap-3 text-sm text-n-slate-12">
                <label class="flex flex-col gap-1.5">
                  <span class="font-medium">
                    {{ localCopy.integrationSelect }}
                  </span>
                  <Select
                    v-model="integrationType"
                    :options="integrationTypeOptions"
                    class="!w-full [&>select]:!w-full"
                  />
                  <span
                    v-if="integrationType === ''"
                    class="text-xs leading-5 text-n-slate-10"
                  >
                    {{ localCopy.noIntegrationHint }}
                  </span>
                </label>

                <div
                  v-if="integrationType === 'banking'"
                  class="overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
                >
                  <div
                    class="flex items-center gap-3 border-b border-n-weak p-3"
                  >
                    <span
                      class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-brand text-white"
                    >
                      <span class="i-lucide-landmark size-4" />
                    </span>
                    <span class="min-w-0 flex-1">
                      <span
                        class="block text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
                      >
                        {{ localCopy.integrationConfiguration }}
                      </span>
                      <span class="mt-0.5 block font-semibold">
                        {{ localCopy.banks }}
                      </span>
                      <span class="mt-0.5 block text-xs text-n-slate-11">
                        {{ localCopy.banksHint }}
                      </span>
                    </span>
                    <span
                      class="rounded-full bg-n-alpha-2 px-2 py-1 text-[11px] font-medium text-n-slate-11"
                    >
                      {{ availableBankCountLabel }}
                    </span>
                  </div>

                  <div class="p-3">
                    <div
                      class="grid grid-cols-2 gap-1 rounded-lg bg-n-alpha-2 p-1"
                    >
                      <label
                        class="cursor-pointer rounded-md px-2.5 py-2 text-center transition-all focus-within:ring-2 focus-within:ring-n-brand/30"
                        :class="
                          formState.integrationLink === 'banking:all_active'
                            ? 'bg-n-solid-1 font-medium text-n-brand shadow-sm'
                            : 'text-n-slate-11 hover:text-n-slate-12'
                        "
                      >
                        <input
                          v-model="formState.integrationLink"
                          type="radio"
                          value="banking:all_active"
                          name="integration-link"
                          class="sr-only"
                        />
                        {{ localCopy.allActiveBanks }}
                      </label>
                      <label
                        class="cursor-pointer rounded-md px-2.5 py-2 text-center transition-all focus-within:ring-2 focus-within:ring-n-brand/30"
                        :class="
                          formState.integrationLink === 'banking:selected'
                            ? 'bg-n-solid-1 font-medium text-n-brand shadow-sm'
                            : 'text-n-slate-11 hover:text-n-slate-12'
                        "
                      >
                        <input
                          v-model="formState.integrationLink"
                          type="radio"
                          value="banking:selected"
                          name="integration-link"
                          class="sr-only"
                        />
                        {{ localCopy.selectBanks }}
                      </label>
                    </div>

                    <p
                      v-if="formState.integrationLink.startsWith('banking:')"
                      class="mt-2 px-1 text-xs text-n-slate-11"
                    >
                      {{
                        formState.integrationLink === 'banking:selected'
                          ? localCopy.selectBanksHint
                          : localCopy.allActiveBanksHint
                      }}
                    </p>

                    <div
                      v-if="formState.integrationLink === 'banking:selected'"
                      class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2"
                    >
                      <label
                        v-for="option in bankOptions"
                        :key="option.provider"
                        class="flex min-h-10 items-center gap-2.5 rounded-lg border px-3 py-2 transition-all focus-within:ring-2 focus-within:ring-n-brand/30"
                        :class="
                          !option.active
                            ? 'cursor-not-allowed border-n-weak bg-n-alpha-1 text-n-slate-10 opacity-70'
                            : formState.selectedBankHookIds.includes(
                                  option.hookIds[0]
                                )
                              ? 'border-n-brand bg-n-brand/10 text-n-brand'
                              : 'cursor-pointer border-n-weak bg-n-solid-1 hover:border-n-strong hover:bg-n-alpha-2'
                        "
                      >
                        <input
                          v-model="formState.selectedBankHookIds"
                          type="checkbox"
                          :value="option.hookIds[0]"
                          :disabled="!option.active"
                          class="sr-only"
                        />
                        <span
                          class="grid size-5 shrink-0 place-content-center rounded border transition-colors"
                          :class="
                            formState.selectedBankHookIds.includes(
                              option.hookIds[0]
                            )
                              ? 'border-n-brand bg-n-brand text-white'
                              : 'border-n-strong bg-n-solid-1'
                          "
                        >
                          <span
                            v-if="
                              formState.selectedBankHookIds.includes(
                                option.hookIds[0]
                              )
                            "
                            class="i-lucide-check size-3.5"
                          />
                        </span>
                        <span
                          class="min-w-0 flex-1 truncate text-xs font-medium"
                        >
                          {{ option.displayLabel }}
                        </span>
                        <span
                          class="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                          :class="
                            option.active
                              ? 'bg-n-teal-3 text-n-teal-11'
                              : 'bg-n-alpha-2 text-n-slate-10'
                          "
                        >
                          {{
                            option.active
                              ? localCopy.activeBank
                              : option.configured
                                ? localCopy.inactiveBank
                                : localCopy.bankNotConnected
                          }}
                        </span>
                      </label>
                    </div>

                    <div
                      v-if="formState.integrationLink === 'banking:selected'"
                      class="mt-2 flex items-center justify-between px-1 text-xs"
                    >
                      <span
                        :class="
                          bankSelectionError
                            ? 'text-n-ruby-9'
                            : 'text-n-slate-11'
                        "
                      >
                        {{
                          bankSelectionError
                            ? localCopy.selectAtLeastOneBank
                            : selectedBankCountLabel
                        }}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="selectedErpOption"
                  class="overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
                >
                  <div
                    class="flex items-center gap-3 border-b border-n-weak p-3"
                  >
                    <span
                      class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-brand text-white"
                    >
                      <span class="i-lucide-database size-4" />
                    </span>
                    <span class="min-w-0 flex-1">
                      <span
                        class="block text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
                      >
                        {{ localCopy.integrationConfiguration }}
                      </span>
                      <span class="mt-0.5 block font-semibold">
                        {{ selectedErpOption.label }}
                      </span>
                      <span class="mt-0.5 block text-xs text-n-slate-11">
                        {{ selectedErpOption.hint || localCopy.providersHint }}
                      </span>
                    </span>
                  </div>
                </div>
              </fieldset>
            </section>

            <section
              class="order-2 flex min-w-0 flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ localCopy.inboxesTitle }}
                </p>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ localCopy.inboxesDescription }}
                </p>
              </div>

              <p
                v-if="!sortedInboxes.length"
                class="mb-0 rounded-lg bg-n-alpha-2 px-3 py-4 text-center text-sm text-n-slate-11"
              >
                {{ localCopy.noInboxes }}
              </p>

              <div v-else class="space-y-4">
                <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                  <span class="font-medium">{{ localCopy.inboxPicker }}</span>
                  <ComboBox
                    v-model="inboxPickerId"
                    :options="inboxPickerOptions"
                    :placeholder="localCopy.inboxPickerPlaceholder"
                    :search-placeholder="localCopy.inboxPickerSearch"
                    :empty-state="localCopy.inboxPickerEmpty"
                    :disabled="!inboxPickerOptions.length"
                    teleport-to-body
                    data-testid="agent-bot-inbox-picker"
                  />
                </label>

                <div>
                  <div class="mb-2 flex items-center gap-2">
                    <p class="mb-0 text-xs font-medium text-n-slate-11">
                      {{ localCopy.selectedInboxes }}
                    </p>
                    <span
                      class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-semibold text-n-slate-10"
                    >
                      {{ selectedInboxes.length }}
                    </span>
                  </div>

                  <p
                    v-if="!selectedInboxes.length"
                    class="mb-0 rounded-lg border border-dashed border-n-weak px-3 py-4 text-center text-xs text-n-slate-10"
                  >
                    {{ localCopy.noSelectedInboxes }}
                  </p>

                  <div
                    v-else
                    class="flex max-h-40 flex-wrap gap-2 overflow-y-auto pr-1"
                  >
                    <span
                      v-for="inbox in selectedInboxes"
                      :key="inbox.id"
                      class="inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium"
                      :class="
                        isInboxAssignedToAnotherBot(inbox)
                          ? 'border-n-amber-6 bg-n-amber-3 text-n-amber-11'
                          : 'border-n-brand/30 bg-n-brand/10 text-n-slate-12'
                      "
                      :title="
                        isInboxAssignedToAnotherBot(inbox)
                          ? inboxAssignmentLabel(inbox)
                          : inbox.name
                      "
                    >
                      <span
                        :class="
                          isInboxAssignedToAnotherBot(inbox)
                            ? 'i-lucide-triangle-alert'
                            : inboxChannelIcon(inbox)
                        "
                        class="size-3.5 shrink-0"
                      />
                      <span class="truncate">{{ inbox.name }}</span>
                      <button
                        type="button"
                        class="ml-0.5 grid size-5 shrink-0 place-content-center rounded-md text-current opacity-70 transition hover:bg-n-alpha-2 hover:opacity-100"
                        :aria-label="`${localCopy.removeInbox}: ${inbox.name}`"
                        @click="removeInbox(inbox.id)"
                      >
                        <span class="i-lucide-x size-3.5" />
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </template>

          <section
            v-if="botSecret || showAccessTokenInput"
            class="order-4 grid grid-cols-1 gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5 sm:grid-cols-2"
          >
            <header class="sm:col-span-2">
              <p class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ localCopy.credentialsTitle }}
              </p>
              <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
                {{ localCopy.credentialsDescription }}
              </p>
            </header>

            <div v-if="botSecret" class="flex flex-col gap-2">
              <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                {{ $t('AGENT_BOTS.SECRET.LABEL') }}
              </label>
              <AccessToken
                :value="botSecret"
                :show-reset-button="type === MODAL_TYPES.EDIT"
                @on-copy="onCopySecret"
                @on-reset="onResetSecret"
              />
            </div>

            <div v-if="showAccessTokenInput" class="flex flex-col gap-2">
              <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                {{ $t('AGENT_BOTS.ACCESS_TOKEN.TITLE') }}
              </label>
              <AccessToken
                :value="accessToken"
                :show-reset-button="type === MODAL_TYPES.EDIT"
                @on-copy="onCopyToken"
                @on-reset="onResetToken"
              />
            </div>
          </section>
        </div>
      </div>

      <div
        class="flex shrink-0 items-center justify-end w-full gap-2 px-0 pt-2"
      >
        <NextButton
          faded
          slate
          type="reset"
          :label="closeButtonLabel"
          :icon="showAccessToken ? 'i-lucide-check' : undefined"
          @click="onClickClose()"
        />
        <NextButton
          v-if="!showAccessToken"
          type="button"
          data-testid="label-submit"
          :label="confirmButtonLabel"
          :is-loading="isLoading"
          :disabled="v$.$invalid || bankSelectionError"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Dialog>
</template>
